import PatientDetailBody from "@/components/body/PatientDetailBody";
import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { CensusData, Patient } from "@/store/modules/patient/types";
import { Permission, userNamespace } from "@/store/modules/user";
import { User } from "@/store/modules/user/types";
import { Alert } from "@/store/types";
import _ from "lodash";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import util, { Colors } from "../shared/util";

@Component({
  components: { PatientDetailBody }
})
export default class BodyColumnData extends Vue {
  @Action("updateSettings", patientNamespace)
  updateSettings!: Function;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Getter("allergies", patientNamespace)
  allergies!: any;

  @Getter("mapAlert", patientNamespace)
  mapAlert!: number;

  @Getter("user", userNamespace)
  user!: User;

  @Getter("demo", userNamespace)
  demo!: boolean;

  @Getter("helpStep", navigationNamespace)
  step!: number;

  @Getter("towerMode", patientNamespace)
  towerMode!: boolean;

  @Getter("area", patientNamespace)
  area!: string;

  @Getter("showGreenGrayOnly", userNamespace)
  showGreenGrayOnly!: any;

  @Prop(Object)
  data!: Patient;

  get medicationLink() {
    if (this.vars && this.vars.system && this.vars.system.hyperlink) {
      // hyperlink: ""https://cas-cst.med.umich.edu/?p=" + BODY.patient.patientID + "&v=" + BODY.patient.visitID"
      const link = this.vars.system.hyperlink
        .replace("BODY.patient.patientID", this.data.census.patientID)
        .replace("BODY.patient.visitID", this.data.census.visitID)
        .replace(/"| /g, "");

      return link.split("+").join("");
    } else {
      return false;
    }
  }

  get isBypass() {
    return _.get(this.data, "body.atoms.bypass.age", -1) >= 0;
  }

  get noteLevel() {
    const defaultValue = this.towerMode ? 0 : null;
    return _.get(this.data, "body.atoms.careNote.value", defaultValue);
  }

  get noteHasText() {
    return _.get(this.data, "body.atoms.careNote.legend", "") != "";
  }

  get hasInfusions() {
    return _.get(this.data, "body.atoms.infusions.age", -1) >= 0;
  }

  get hasWaves() {
    return _.get(this.data, "census.hasWaves", "");
  }

  get volume() {
    const atoms = this.data.body.atoms;
    if (!atoms.ioBalance) {
      return "";
    }
    const ioBalanceText =
      (atoms.ioBalance.value > 0 ? "+" : "") + atoms.ioBalance.value.toFixed(0);
    return ioBalanceText;
  }

  get postOpReviewTaskCount() {
    return this.data.body &&
      this.data.body.postOpReview &&
      this.data.body.postOpReview.TaskRatio
      ? this.data.body.postOpReview.TaskRatio
      : "?/?";
  }

  atomValue(atomName: string) {
    return this.data.body.atoms[atomName]
      ? this.data.body.atoms[atomName].value
      : "";
  }

  buttonColor(type: string, target: string | Array<string>) {
    if (type === "risk") {
      if (typeof target === "string") {
        target = [target];
      }
      const risk = this.data.body.risk;
      const intersection = _.intersection(risk, target);
      return intersection.length ? Colors.Orange : Colors.Gray;
    } else if (type === "atom") {
      const atoms = this.data.body.atoms;
      if (typeof target === "string" && atoms[target]) {
        const level = atoms[target].level;
        if (level === 0) {
          return target == "ioBalance" && atoms[target].age != -1
            ? Colors.Green
            : Colors.Gray;
        } else {
          return util.getLevelColor(level, this.showGreenGrayOnly);
        }
      }
    } else if (type === "PostOpReviewStatus") {
      if (
        this.data.body &&
        this.data.body.postOpReview &&
        this.data.body.postOpReview.Status === 2
      ) {
        // tasks are reviewed, but not complete
        return Colors.Yellow;
      } else if (
        this.data.body &&
        this.data.body.postOpReview &&
        this.data.body.postOpReview.Status === 3
      ) {
        // tasks are reviewed and all required tasks are complete
        return Colors.Green;
      } else {
        // Status 1, tasks have not been reviewed
        return Colors.Gray;
      }
    }
    return Colors.Gray;
  }

  buttonIcon(atom: string) {
    const alert = this.data.census.alert.filter(f => f.atom == atom)[0];
    const icon =
      alert != null && alert.icon !== null
        ? alert.icon.replace(".png", "Inv.svg")
        : "";

    return icon;
  }

  isDarkButton(type: string, target: string | Array<string>) {
    const color = this.buttonColor(type, target);
    return color === Colors.Red || color === Colors.Green;
  }
  get atRiskAllergies() {
    return this.$store.getters[`${patientNamespace.namespace}/atRisk`](
      "Allergies"
    );
  }

  get atRiskLatex() {
    return this.$store.getters[`${patientNamespace.namespace}/atRisk`]("Latex");
  }

  get allergiesColor() {
    return this.allergies && this.allergies.level
      ? this.allergies.level == 6
        ? Colors.Orange
        : this.allergies.level == 3
        ? Colors.Green
        : Colors.Gray
      : Colors.Gray;
  }

  get allergiesTextColor() {
    return this.allergiesColor === Colors.Green;
  }

  get allergiesCount() {
    return this.allergies && this.allergies.value > 0
      ? this.allergies.list.length
      : 0;
  }

  get allergiesText() {
    if (this.allergies && this.allergies.value > 0) {
      const count = this.allergies.list.length;
      return `: ${count}`;
    }
    // Case where the only allergy is "No Known Allergies" or "No documented allergies"
    return "";
  }

  get allergiesExtraText() {
    let text = "";
    if (this.allergiesCount > 0 && this.allergiesCount < 3) {
      this.allergies.list.forEach((a: any) => {
        text += "<br />" + a.label.substr(0, 15);
      });
    }
    return text;
  }

  @Action("showPopup", navigationNamespace)
  showPopup!: Function;

  get hasSofa() {
    return !!this.data.body.atoms.sofa;
  }

  get hasQSofa() {
    return (
      !!this.data.body.atoms.qSofa && this.data.body.atoms.qSofa.legend !== ""
    );
  }

  get hasDeteriorationIndex() {
    return (
      !!this.data.body.atoms.deteriorationIndex &&
      this.data.body.atoms.deteriorationIndex.age !== -1
    );
  }

  get hasPACUTaskList() {
    return this.area === "awpacu";
  }

  get careNoteIconValue() {
    if (_.get(this, "data.body.current.CareCoordinationNote.value") > 0) {
      return "3";
    } else {
      return "0";
    }
  }

  get infusionExtraText() {
    return "";
  }

  get infusionText() {
    if (this.area === "awob" && this.hasInfusions) {
      const mg = _.get(this, "data.body.current.MagnesiumFluid.age") > 0;
      const ins = _.get(this, "data.body.current.Insulin.age") > 0;
      const ox = _.get(this, "data.body.current.Oxytocin.age") > 0;

      const inf = [];
      if (mg) inf.push("Mg");
      if (ins) inf.push("Ins");
      if (ox) inf.push("Ox");

      return ": " + inf.join(",");
    } else if (this.hasInfusions) {
      const inf = _.get(this.data, "body.atoms.infusions.legend", "");
      const count = inf.split("|").length;
      return ": " + count;
    } else {
      return "";
    }
  }

  constructor() {
    super(...arguments);

    for (let i = 5; i <= 105; i = i + 5) {
      this.mapAlerts.push(i);
    }
    this.mapAlerts = this.mapAlerts.map(entry => {
      return { text: `< ${entry}`, value: entry };
    });
  }

  mapAlerts: Array<any> = [];

  updateMapAlert(value: number) {
    if (this.demo) {
      return;
    }
    this.updateSettings({
      id: this.data.census.roomSys,
      settings: { BPRed: value }
    });
  }

  get canAlter() {
    if (this.user.rights.includes(Permission.NO_EDIT)) {
      return false;
    } else if (this.user.rights.includes(Permission.ADMIN)) {
      return true;
    }

    const census: CensusData = this.data.census;

    // in the room
    if (this.user.room === census.room) {
      return true;
    }

    // one of the staff logged into the case.
    if (
      census.staff
        .map(s => s.userID.toLowerCase())
        .includes(this.user.userID.toLowerCase())
    ) {
      return true;
    }

    return false;
  }
  cannotEditToolTip(field: string = "") {
    return !this.canAlter
      ? {
          content: "Current user does not have permission to " + field + ".",
          openOnHover: true,
          nudgeLeft: true
        }
      : {};
  }

  get areaMap() {
    const withoutCurrentArea = { ...this.data.census.areaMap };
    delete withoutCurrentArea[this.area];
    return withoutCurrentArea;
  }

  get towerModeAlerts() {
    const hasCheck =
      this.data.census.alert.filter(a => a.atom === "check").length >= 1;
    const alerts: Array<Alert> = _.cloneDeep(this.data.census.alert);
    if (!hasCheck) {
      // Add default check to icon list to allow users to add new issues
      // when there are no alerts
      alerts.push({
        atom: "check",
        icon: "alertCheck1.png",
        id: "Check.1",
        level: 0,
        link: "tower",
        message: "Default checkmark",
        pickID: "",
        reviewStatus: 0,
        room: "",
        slot: 99,
        timeActive: "",
        audioMessage: ""
      });
    }
    return alerts;
  }
}
